import VectorSvg from '@allesauto/design/svg/Vector.svg';
import {
  CtaTeaserTypes,
  DynamicIconType,
  FieldTypeAsset,
  FieldTypeLink,
  HeadlineVariation,
} from '@allesauto/utils/consts';
import {
  getUrlFromStoryblokLink,
  isEmptyObject,
  parseSlashToURL,
} from '@allesauto/utils/funcs';
import { sbEditable } from '@storyblok/storyblok-editable';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { FC, ReactNode } from 'react';

import BenefitIcon from '../benefitIcon';
import Button from '../button';
import Headline from '../headline';
import InputField from '../inputField';
import NextImage from '../nextImage';

export interface CtaTeaserProps {
  teaserType: CtaTeaserTypes;
  icon?: ReactNode;
  iconCustom?: DynamicIconType;
  image?: FieldTypeAsset;
  imageBigVersion?: boolean;
  headline: string;
  description?: string;
  inputLabel?: string;
  ctaLabel?: string;
  mailToEmail?: string;
  ctaLink?: FieldTypeLink;
  ctaAction?: () => void;
  className?: string;
  bgClassName?: string;
  textClassName?: string;
  idButton?: string;
  component: string;
  _uid: string;
  _editable?: string;
}

const CtaTeaser: FC<React.PropsWithChildren<CtaTeaserProps>> = (props) => {
  const {
    teaserType,
    icon = <VectorSvg />,
    iconCustom,
    image,
    headline,
    description,
    ctaLabel,
    imageBigVersion = false,
    inputLabel,
    mailToEmail,
    ctaLink,
    ctaAction,
    className: customClassName,
    bgClassName = 'bg-purple80 monochrome:bg-grey90',
    textClassName = 'text-white monochrome:!text-white',
    children,
    idButton,
  } = props;
  const router = useRouter();

  const commonClass = {
    container: clsx(bgClassName, customClassName),
    description: clsx('font-regular font-weight-normal text-p1', textClassName),
  };

  const ctaActionSelector = () => {
    if (ctaLink && !isEmptyObject(ctaLink)) {
      const url = getUrlFromStoryblokLink(ctaLink);
      const openTab = ctaLink.target === '_blank';

      const parsedUrl = parseSlashToURL(url);
      openTab ? window.open(parsedUrl, '_blank') : router.push(parsedUrl);
    }

    return ctaAction && ctaAction();
  };

  if (teaserType === CtaTeaserTypes.FRAME) {
    const frameTeaserClass = {
      container: clsx(
        commonClass.container,
        'grid grid-cols-24 h-auto py-[50px] md:py-100px'
      ),
      contentContainer:
        'col-end-24 col-start-2 grid grid-cols-1 pb-30px px-15px relative md:grid-cols-22 md:p-0',
      frame: clsx(
        'absolute border-7px h-[calc(100%-30px)] w-full md:h-full md:w-[82%]',
        'bottom-0 left-0 z-10 md:left-[unset] md:right-0',
        'border-green60 monochrome:border-grey90'
      ),
      headline: clsx(
        'mb-20px px-15px z-20 sm:mb-0 sm:pb-20px md:my-60px md:pl-0 md:pr-20px md:py-20px',
        'h-max md:col-end-10 md:col-start-1',
        bgClassName
      ),
      textContainer:
        'px-15px z-20 md:pl-0 md:py-80px md:pr-60px md:col-start-11 md:col-end-24 md:flex md:flex-col md:justify-center',
      ctaContainer:
        'flex md:justify-between gap-30px md:gap-60px flex-col md:flex-row md:flex-wrap',
      input: 'md:mb-0 w-full md:w-[300px]',
      description: clsx(commonClass.description, 'mb-30px'),
      button:
        'w-full md:w-[300px] !px-20px monochrome:text-black wl-bg-highlight wl-border-highlight wl-bg-highlight--hover wl-text-highlight--hover wl-border-highlight--hover',
    };

    const ctaActionFrameSelector = () => {
      if (ctaLink && !isEmptyObject(ctaLink)) {
        const url = getUrlFromStoryblokLink(ctaLink);
        const openTab = ctaLink.target === '_blank';
        const parsedUrl = parseSlashToURL(url);

        openTab ? window.open(parsedUrl, '_blank') : router.push(parsedUrl);
      }

      if (mailToEmail) {
        return (document.location = `mailto:${mailToEmail}`);
      }

      return ctaAction && ctaAction();
    };

    return (
      <div
        {...sbEditable(props)}
        className={frameTeaserClass.container}
        data-hide-for-print=""
      >
        <div className={frameTeaserClass.contentContainer}>
          <Headline
            headlineTag={HeadlineVariation.H2}
            headlineColor={textClassName}
            headlineText={headline}
            className={frameTeaserClass.headline}
          />
          <div className={frameTeaserClass.textContainer}>
            {!!description && (
              <p
                dangerouslySetInnerHTML={{
                  __html: description,
                }}
                className={frameTeaserClass.description}
              ></p>
            )}
            {children ? (
              children
            ) : (
              <div className={frameTeaserClass.ctaContainer}>
                {!!inputLabel && (
                  <InputField
                    placeholder={inputLabel}
                    darkBg
                    type="email"
                    customStyle={frameTeaserClass.input}
                  />
                )}
                {!!ctaLabel && (
                  <Button
                    text={ctaLabel}
                    type="button"
                    onClick={ctaActionFrameSelector}
                    customClassName={frameTeaserClass.button}
                    whitelabel={true}
                  />
                )}
              </div>
            )}
          </div>
          <div className={frameTeaserClass.frame}></div>
        </div>
      </div>
    );
  } else if (teaserType === CtaTeaserTypes.ICON) {
    const iconTeaserClass = {
      container: clsx(
        commonClass.container,
        'grid grid-cols-1 px-30px py-30px md:grid-cols-24 md:px-0 md:py-60px'
      ),
      iconContainer: clsx(
        'mb-40px md:col-end-4 md:col-start-2 md:mb-0 md:mt-15px',
        iconCustom && 'md:mt-0',
        textClassName
      ),
      iconWrapper: `h-60px relative w-60px md:h-[90px] md:w-[90px] wl-cta-icon-color md:mx-auto`,
      headline: 'mb-10px md:mb-0 md:col-start-5 md:col-end-13',
      textContainer: 'md:col-start-15 md:col-end-24',
      description: clsx(commonClass.description, 'mb-30px'),
      button: 'w-full md:w-auto aurego-color1',
    };

    return (
      <div {...sbEditable(props)} className={iconTeaserClass.container}>
        <div className={iconTeaserClass.iconContainer}>
          <div className={iconTeaserClass.iconWrapper}>
            {iconCustom ? (
              <div className="flex h-full items-center justify-center">
                <BenefitIcon
                  icon={iconCustom as DynamicIconType}
                  isNumeric={true}
                  customClassName="!w-[120px] !h-[120px]"
                />
              </div>
            ) : (
              icon
            )}
          </div>
        </div>
        <div className={iconTeaserClass.headline}>
          <Headline
            headlineTag={HeadlineVariation.H2}
            headlineColor={textClassName}
            headlineText={headline}
          />
        </div>
        <div className={iconTeaserClass.textContainer}>
          {!!description && (
            <p
              dangerouslySetInnerHTML={{
                __html: description,
              }}
              className={iconTeaserClass.description}
            ></p>
          )}
          {children ? (
            children
          ) : (
            <>
              {!!ctaLabel && (
                <Button
                  text={ctaLabel}
                  type="button"
                  onClick={ctaActionSelector}
                  customClassName={iconTeaserClass.button}
                  whitelabel={true}
                  id={idButton}
                />
              )}
            </>
          )}
        </div>
      </div>
    );
  } else {
    const imageTeaserClass = {
      container: clsx(
        commonClass.container,
        'grid grid-cols-1 min-h-[460px] px-15px py-[50px] md:flex-row md:grid-cols-24 md:px-0'
      ),
      imageContainer: clsx(
        '-ml-15px col-start-1 h-[180px] relative md:flex-1 md:h-auto md:ml-0',
        !imageBigVersion && 'md:col-end-10',
        imageBigVersion && 'md:col-end-12'
      ),
      image:
        'transform-gpu w-full absolute left-0 -top-[50px] -bottom-[30px] md:-bottom-[50px]',
      textContainer: clsx(
        'flex flex-1 flex-col justify-center mt-30px md:mt-0',
        !imageBigVersion && 'md:col-start-12 md:col-end-23',
        imageBigVersion && 'md:col-start-14 md:col-end-24'
      ),
      headline: 'mb-40px',
      description: clsx(commonClass.description, 'mb-20px'),
      button: 'w-full md:w-max md:min-w-[240px]',
    };

    const imageUrl = image?.filename ?? null;

    return (
      <div {...sbEditable(props)} className={imageTeaserClass.container}>
        <div className={imageTeaserClass.imageContainer}>
          {imageUrl && (
            <NextImage
              useCustomLoader={false}
              alt={image?.alt || ''}
              src={imageUrl}
              className={imageTeaserClass.image}
              layout="fill"
              objectFit="contain"
              objectPosition="left"
            />
          )}
        </div>
        <div className={imageTeaserClass.textContainer}>
          <Headline
            headlineTag={HeadlineVariation.H2}
            headlineColor={textClassName}
            headlineText={headline}
            className={imageTeaserClass.headline}
          />
          {!!description && (
            <p
              dangerouslySetInnerHTML={{
                __html: description,
              }}
              className={imageTeaserClass.description}
            ></p>
          )}
          {children ? (
            children
          ) : (
            <>
              {!!ctaLabel && (
                <Button
                  text={ctaLabel}
                  type="button"
                  onClick={ctaActionSelector}
                  customClassName={imageTeaserClass.button}
                  whitelabel={true}
                />
              )}
            </>
          )}
        </div>
      </div>
    );
  }
};

export default CtaTeaser;
